import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { markTourSeen } from '../lib/api';
import { 
  Compass, Inbox, Clock, Star, MessageSquare, Flag, Coffee, 
  ArrowRight, ArrowLeft, Check, X
} from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Maris Desk',
    description: 'Your calm, operator-first guest desk. Let me show you around.',
    icon: Compass,
    color: 'bg-[#3F617E] text-white'
  },
  {
    id: 'today',
    title: 'Today — Your Work Queue',
    description: 'Start here every day. See urgent items, due soon tasks, and follow-ups — all sorted by priority.',
    icon: Clock,
    color: 'bg-amber-100 text-amber-700'
  },
  {
    id: 'inbox',
    title: 'Inbox — Smart Triage',
    description: 'Messages are auto-tagged (Urgent, Time-sensitive, Sales, Info) so you know what needs attention first.',
    icon: Inbox,
    color: 'bg-blue-100 text-blue-700'
  },
  {
    id: 'draft',
    title: 'AI Draft Replies',
    description: 'Click "Draft reply" and Maris generates a warm, on-brand response using your property\'s voice.',
    icon: MessageSquare,
    color: 'bg-green-100 text-green-700'
  },
  {
    id: 'escalate',
    title: 'Escalate with Care',
    description: 'Need to involve the owner or maintenance? Escalate creates a task and drafts a calm guest update.',
    icon: Flag,
    color: 'bg-red-100 text-red-700'
  },
  {
    id: 'calm',
    title: 'Calm Mode',
    description: 'Toggle Calm Mode to hide the noise and focus only on what needs action right now.',
    icon: Coffee,
    color: 'bg-stone-100 text-stone-700'
  },
  {
    id: 'reviews',
    title: 'Reviews & Reports',
    description: 'Draft review replies, track conversion rates, and export PDF reports for property owners.',
    icon: Star,
    color: 'bg-purple-100 text-purple-700'
  },
  {
    id: 'done',
    title: 'You\'re all set!',
    description: 'Your guest desk is ready. Start with the Priority inbox — those guests are waiting.',
    icon: Check,
    color: 'bg-green-100 text-green-700'
  }
];

export const Tour = ({ show, onComplete, user }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Parent (DeskLayout) decides when to open the tour — either automatically
    // for first-time users, or manually via the "Take tour again" menu item.
    // The tour itself just honors the `show` prop.
    if (!show || !user) {
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
    setCurrentStep(0);
  }, [show, user]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    try {
      await markTourSeen();
      // Update local storage
      const storedUser = JSON.parse(localStorage.getItem('maris_user') || '{}');
      storedUser.hasSeenTour = true;
      localStorage.setItem('maris_user', JSON.stringify(storedUser));
    } catch (error) {
      console.error('Failed to mark tour as seen:', error);
    }
    setIsOpen(false);
    onComplete?.();
  };

  const handleFinish = async () => {
    try {
      await markTourSeen();
      // Update local storage
      const storedUser = JSON.parse(localStorage.getItem('maris_user') || '{}');
      storedUser.hasSeenTour = true;
      localStorage.setItem('maris_user', JSON.stringify(storedUser));
    } catch (error) {
      console.error('Failed to mark tour as seen:', error);
    }
    setIsOpen(false);
    onComplete?.();
  };

  const step = TOUR_STEPS[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden" hideClose>
        {/* Progress bar */}
        <div className="h-1 bg-stone-100">
          <div 
            className="h-full bg-[#3F617E] transition-all duration-300"
            style={{ width: `${((currentStep + 1) / TOUR_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Skip button */}
          {!isLastStep && (
            <button 
              onClick={handleSkip}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}

          {/* Icon */}
          <div className={`w-14 h-14 rounded-2xl ${step.color} flex items-center justify-center mb-4`}>
            <Icon className="h-7 w-7" />
          </div>

          {/* Step indicator */}
          <Badge variant="secondary" className="mb-3 text-xs">
            {currentStep + 1} of {TOUR_STEPS.length}
          </Badge>

          {/* Title & Description */}
          <h2 className="text-xl font-heading font-medium text-stone-900 mb-2">
            {step.title}
          </h2>
          <p className="text-stone-600 text-sm leading-relaxed">
            {step.description}
          </p>
        </div>

        {/* Footer */}
        <div className="p-4 bg-stone-50 border-t border-stone-100 flex items-center justify-between">
          <div>
            {!isFirstStep && !isLastStep && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrev}
                className="text-stone-600"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            {isFirstStep && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="text-stone-500"
              >
                Skip tour
              </Button>
            )}
          </div>

          <Button
            onClick={handleNext}
            className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
          >
            {isLastStep ? (
              <>
                Get started
                <Check className="h-4 w-4 ml-1" />
              </>
            ) : (
              <>
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center gap-1.5 pb-4">
          {TOUR_STEPS.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentStep(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentStep 
                  ? 'bg-[#3F617E] w-4' 
                  : idx < currentStep 
                    ? 'bg-[#3F617E]/40' 
                    : 'bg-stone-200'
              }`}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Tour;
